<template>
  <div class="toast-container">
    <TransitionGroup name="toast">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        v-show="toast.visible"
        :class="['toast', `toast-${toast.type}`]"
      >
        <div class="toast-content">
          <div class="toast-icon">
            <v-icon v-if="toast.type === 'success'" size="24">mdi-check-circle</v-icon>
            <v-icon v-else-if="toast.type === 'error'" size="24">mdi-close-circle</v-icon>
            <v-icon v-else-if="toast.type === 'warning'" size="24">mdi-alert-circle</v-icon>
            <v-icon v-else size="24">mdi-information</v-icon>
          </div>
          <span class="toast-message">{{ toast.message }}</span>
          <button class="toast-close" @click="removeToast(toast.id)">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup>
import { useToast } from '@/composables/useToast';

const { toasts, removeToast } = useToast();
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: calc(var(--app-notification-top, 0px) + 8px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 12000;
  width: 90%;
  max-width: 600px;
  pointer-events: none;
}

.toast {
  background: white;
  margin-bottom: 10px;
  padding: 16px 20px;
  border-radius: 8px;
  border: 1px solid rgba(15, 23, 42, 0.18);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.24);
  pointer-events: auto;
  transition: all 0.3s ease;
}

.toast-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toast-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-message {
  flex: 1;
  font-size: 15px;
  font-weight: 500;
  color: #333;
}

.toast-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition: color 0.2s;
}

.toast-close:hover {
  color: #333;
}

.toast-close:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
  border-radius: 4px;
}

/* Success toast - matching your screenshot */
.toast-success {
  background: #d4f4dd;
  border-left: 4px solid #4caf50;
}

.toast-success .toast-icon {
  color: #4caf50;
}

.toast-success .toast-message {
  color: #2e7d32;
}

/* Error toast */
.toast-error {
  background: #ffebee;
  border-left: 4px solid #f44336;
}

.toast-error .toast-icon {
  color: #f44336;
}

.toast-error .toast-message {
  color: #c62828;
}

/* Warning toast */
.toast-warning {
  background: #fff3e0;
  border-left: 4px solid #ff9800;
}

.toast-warning .toast-icon {
  color: #ff9800;
}

.toast-warning .toast-message {
  color: #e65100;
}

/* Info toast */
.toast-info {
  background: #e3f2fd;
  border-left: 4px solid #2196f3;
}

.toast-info .toast-icon {
  color: #2196f3;
}

.toast-info .toast-message {
  color: #1565c0;
}

/* Animation */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.toast-move {
  transition: transform 0.3s;
}

@media (max-width: 600px) {
  .toast-container {
    width: calc(100% - 24px);
  }

  .toast {
    padding: 14px 16px;
  }
}
</style>
